// Adapter that lets the shared dashboard ChartCard operate against the parallel report chart
// API. Passed to ChartCard via its `adapter` prop. Stable module-level identity so the hook
// wrappers it exposes satisfy the Rules of Hooks.
import useReportChartData from 'api/hooks/use-report-chart-data'
import {
  useReportCharts,
  useCreateReportChart,
  useUpdateReportChart,
  useDestroyReportChart
} from 'api/hooks/use-report-charts'

export const REPORT_CHART_ADAPTER = {
  useChartData: ({ containerId, chartId, params, options }) =>
    useReportChartData({ reportId: containerId, chartId, params, options }),
  useCreateChart: ({ containerId, options }) => useCreateReportChart({ reportId: containerId, options }),
  useUpdateChart: ({ options }) => useUpdateReportChart({ options }),
  useDestroyChart: ({ chartId, options }) => useDestroyReportChart({ id: chartId, options }),
  buildPayload: ({ chartId, chartType, title, params, gridRowId, gridColumnPosition, dataSourceId, extra }) => ({
    id: chartId,
    chart_type: chartType,
    title,
    params,
    ...(extra || {}),
    // The dataset the card has bound (or re-bound, for a chart whose source was removed and a
    // new one picked). Omitted when unset so a decorative block doesn't send a null the API's
    // presence validation would have to interpret.
    ...(dataSourceId ? { report_dataset_id: dataSourceId } : {}),
    ...(gridRowId && { report_grid_row_id: gridRowId }),
    ...(gridColumnPosition !== null && gridColumnPosition !== undefined && {
      grid_column_position: gridColumnPosition
    })
  }),
  invalidate: (queryClient, containerId) => {
    queryClient.invalidateQueries([`reports/${containerId}/report_charts`])
    queryClient.invalidateQueries([`reports/${containerId}/report_grid_rows`])
    queryClient.invalidateQueries(['report-chart-data', containerId])
  },
  allowCsvExport: false,
  // The report goes along because a child report renders its parent's charts over its own slice.
  drillDownParams: ({ chartId, containerId }) => ({ report_chart_id: chartId, report_id: containerId }),
  // Endpoint the pivot/data-table use for pagination + sort ("load more"). Report charts live at
  // report_charts/:id/data, not the dashboard charts/:id/data path.
  chartDataPath: (chartId) => `report_charts/${chartId}/data`,
  // Folded into the chart's request params so it reaches *every* data request, including the
  // pivot/data-table "load more" and sort calls that build their own requests from those params.
  // A child report renders its parent's charts, so without it the server falls back to the parent
  // and page two of a table shows the parent's full, unsliced data.
  dataRequestParams: (containerId) => (containerId ? { report_id: containerId } : {}),
  chartPath: (chartId) => `report_charts/${chartId}`,
  summaryPath: (chartId) => `report_charts/${chartId}/text_summary`,
  useSiblingCharts: ({ containerId }) => useReportCharts({ reportId: containerId }),
  summaryDataOptions: ({ dataSources }) =>
    dataSources.map((source) => ({ kind: 'dataset', id: source.id, label: source.label }))
}
